import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { Button } from '../components/ui/button';
import { ArrowLeft, Users, Target, Heart, Zap, Star, ArrowRight } from 'lucide-react';
import { usePageMeta } from '../hooks/usePageMeta';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15, delayChildren: 0.2 }
  }
};

// Team Member Card Component
const TeamMemberCard = ({ name, title, bio, delay, color }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay }}
      whileHover={{ y: -5 }}
      className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow duration-300"
    >
      {/* Avatar placeholder with gradient */}
      <motion.div
        whileHover={{ scale: 1.05, rotate: 3 }}
        className="w-20 h-20 rounded-2xl mb-6 flex items-center justify-center text-white text-2xl font-bold"
        style={{ background: `linear-gradient(135deg, ${color} 0%, ${color}99 100%)` }}
      >
        {name.split(' ').map(n => n[0]).join('')}
      </motion.div>
      
      <h3 className="text-xl font-bold text-gray-900 mb-1">{name}</h3>
      <p className="text-[#1E3A8A] font-medium mb-4">{title}</p>
      <p className="text-gray-600 leading-relaxed text-sm">{bio}</p>
    </motion.div>
  );
};

// Value Card Component
const ValueCard = ({ icon: Icon, title, description, delay, color }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, scale: 0.9 }}
      animate={isInView ? { opacity: 1, scale: 1 } : {}}
      transition={{ duration: 0.5, delay }}
      whileHover={{ y: -5, scale: 1.02 }}
      className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 text-center group"
    >
      <motion.div
        whileHover={{ rotate: [0, -10, 10, 0] }}
        transition={{ duration: 0.5 }}
        className="w-16 h-16 rounded-xl mx-auto mb-4 flex items-center justify-center"
        style={{ background: `linear-gradient(135deg, ${color}20 0%, ${color}10 100%)` }}
      >
        <Icon className="w-8 h-8" style={{ color }} />
      </motion.div>
      <h3 className="text-lg font-bold text-gray-900 mb-2">{title}</h3>
      <p className="text-gray-600 text-sm leading-relaxed">{description}</p>
    </motion.div>
  );
};

const TeamPage = () => {
  usePageMeta({
    title: 'Meet the Team | BM-Assist.co',
    description: 'Learn about the experienced marketing professionals behind BM-Assist.co, dedicated to helping local businesses launch their marketing.',
    ogTitle: 'Meet the Team | BM-Assist.co',
    ogDescription: 'Meet our team and discover the mission driving affordable marketing solutions for small businesses.'
  });

  const teamMembers = [
    {
      name: "Joe B",
      title: "Founder & CEO",
      bio: "A decade of experience in digital marketing, operations, and automation. Joe built BM-Assist to bridge the gap between enterprise marketing and small-business accessibility — real results without inflated retainers.",
      color: "#1E3A8A"
    },
    {
      name: "Ariana Wells",
      title: "Director of Client Operations",
      bio: "MBA from University of Florida with 8+ years in marketing operations. Ariana transforms chaos into workflow, ensuring every project moves seamlessly from intake to delivery.",
      color: "#F59E0B"
    },
    {
      name: "Marcus Jordan",
      title: "Performance & Ad Launch Lead",
      bio: "MBA in Digital Strategy from Howard University. MJ has helped 200+ local businesses build their first digital footprints, making paid ads understandable for everyday owners.",
      color: "#10B981"
    }
  ];

  const values = [
    {
      icon: Target,
      title: "Project-Based",
      description: "We believe in one-time projects, not endless retainers. Get what you need, own it forever.",
      color: "#1E3A8A"
    },
    {
      icon: Heart,
      title: "Built for Trades",
      description: "We understand contractors, lawn care pros, and home service businesses. We speak your language.",
      color: "#EF4444"
    },
    {
      icon: Zap,
      title: "Fast Turnaround",
      description: "Most projects completed in 24-48 hours. Your time is money, and we respect that.",
      color: "#F59E0B"
    },
    {
      icon: Star,
      title: "Quality First",
      description: "Professional-grade materials that make your business look established and trustworthy.",
      color: "#8B5CF6"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Navigation */}
      <motion.nav 
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
        className="fixed top-0 w-full bg-white/90 backdrop-blur-md border-b border-gray-100 z-50"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="text-2xl font-bold bg-gradient-to-r from-[#1E3A8A] to-[#3B82F6] bg-clip-text text-transparent">
              BM-Assist.co
            </Link>
            <Link to="/">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button variant="outline" className="border-[#1E3A8A] text-[#1E3A8A] hover:bg-[#1E3A8A] hover:text-white rounded-full">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Home
                </Button>
              </motion.div>
            </Link>
          </div>
        </div>
      </motion.nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        {/* Background decoration */}
        <motion.div
          className="absolute top-20 right-0 w-96 h-96 bg-blue-100 rounded-full filter blur-3xl opacity-50"
          animate={{ x: [0, 30, 0], y: [0, -20, 0] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="absolute bottom-0 left-0 w-72 h-72 bg-amber-100 rounded-full filter blur-3xl opacity-50"
          animate={{ x: [0, -20, 0], y: [0, 20, 0] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="inline-flex items-center gap-2 bg-[#1E3A8A]/10 text-[#1E3A8A] px-4 py-2 rounded-full text-sm font-semibold mb-6"
            >
              <Users className="w-4 h-4" />
              Meet the Team
            </motion.div>
            
            <h1 className="text-5xl sm:text-6xl font-bold mb-6 leading-tight">
              <span className="text-gray-900">The people behind </span>
              <span className="bg-gradient-to-r from-[#1E3A8A] to-[#3B82F6] bg-clip-text text-transparent">
                BM-Assist
              </span>
            </h1>
            
            <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
              A remote-first team of marketers, strategists, and automation pros with agency expertise 
              and startup agility — dedicated to helping local businesses thrive.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center"
          >
            <motion.span 
              variants={fadeInUp}
              className="inline-block px-4 py-2 bg-amber-100 text-amber-700 rounded-full text-sm font-semibold mb-4"
            >
              Our Mission
            </motion.span>
            
            <motion.h2 
              variants={fadeInUp}
              className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6"
            >
              Making professional marketing{' '}
              <span className="text-[#1E3A8A]">accessible</span>
            </motion.h2>
            
            <motion.p 
              variants={fadeInUp}
              className="text-lg text-gray-600 leading-relaxed"
            >
              BM-Assist was built to make professional-grade marketing accessible for every kind of 
              local business — from food trucks to construction firms. We believe small operations 
              deserve the same polish, clarity, and systems that large brands enjoy. That's why we 
              deliver powerful one-time projects that drive lasting impact, without the agency price tag.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-12"
          >
            <motion.h2 
              variants={fadeInUp}
              className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4"
            >
              What drives us
            </motion.h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <ValueCard key={value.title} {...value} delay={index * 0.1} />
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-12"
          >
            <motion.span 
              variants={fadeInUp}
              className="inline-block px-4 py-2 bg-[#1E3A8A]/10 text-[#1E3A8A] rounded-full text-sm font-semibold mb-4"
            >
              Leadership
            </motion.span>
            <motion.h2 
              variants={fadeInUp}
              className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4"
            >
              Meet the core team
            </motion.h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {teamMembers.map((member, index) => (
              <TeamMemberCard key={member.name} {...member} delay={index * 0.15} />
            ))}
          </div>
        </div>
      </section>

      {/* Join Team CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-[#1E3A8A] to-[#3B82F6]">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Want to join our team?
            </h2>
            <p className="text-xl text-blue-100 mb-8">
              We're always looking for talented marketers who want to help local businesses succeed.
            </p>
            <Link to="/applications">
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="inline-block"
              >
                <Button className="bg-white text-[#1E3A8A] hover:bg-gray-100 text-lg px-8 py-6 rounded-full shadow-xl font-semibold">
                  View Open Positions
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </motion.div>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-4 md:mb-0">
              <span className="text-xl font-bold bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent">
                BM-Assist.co
              </span>
            </div>
            <div className="flex gap-6 text-sm text-gray-400">
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
              <Link to="/cookies" className="hover:text-white transition-colors">Cookies</Link>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-500 text-sm">
            © {new Date().getFullYear()} BM-Assist.co. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TeamPage;
